import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import { Upload, Info, AlertCircle, AlertTriangle, Check, X } from 'lucide-react';
import { workspaceApi } from '../../services/api';
import './GeneralSettings.css';

function GeneralSettings() {
    const { currentWorkspace, switchWorkspace } = useWorkspace();
    const { refreshUser } = useAuth();
    const [name, setName] = useState(currentWorkspace?.name || 'Acme Testing');
    const [slug, setSlug] = useState(currentWorkspace?.slug || 'acme-testing');
    const [primaryOS, setPrimaryOS] = useState('ios');
    const [orientation, setOrientation] = useState('portrait');
    const [retention, setRetention] = useState('90');
    const [hasChanges, setHasChanges] = useState(false);
    const [saving, setSaving] = useState(false);

    // Update local state if context changes (e.g. initial load)
    useEffect(() => {
        if (currentWorkspace) {
            setName(currentWorkspace.name);
            setSlug(currentWorkspace.slug);
            if (currentWorkspace.settings) {
                setRetention(currentWorkspace.settings.rotation_retention || '90');
                if (currentWorkspace.settings.primaryOS) setPrimaryOS(currentWorkspace.settings.primaryOS);
                if (currentWorkspace.settings.orientation) setOrientation(currentWorkspace.settings.orientation);
            }
        }
    }, [currentWorkspace]);

    // Track changes
    const handleChange = (setter, value) => {
        setter(value);
        setHasChanges(true); // Simplified change tracking
    };

    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmationName, setDeleteConfirmationName] = useState('');
    const [deleting, setDeleting] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const response = await workspaceApi.updateWorkspace(currentWorkspace.id, {
                name,
                slug, // Note: Backend handles validation if changed
                settings: {
                    rotation_retention: retention
                },
                primaryOS,
                orientation
            });

            // Update context with returned workspace
            if (response.data && response.data.workspace) {
                switchWorkspace(response.data.workspace);
                await refreshUser(); // Refresh global list
            }

            setSuccessMessage('Settings saved successfully');
            setHasChanges(false);

            // Clear success message after 3 seconds
            setTimeout(() => setSuccessMessage(null), 3000);

        } catch (err) {
            console.error('Failed to save settings:', err);
            setError(err.response?.data?.error || 'Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteWorkspace = async () => {
        if (deleteConfirmationName !== currentWorkspace.name) return;

        setDeleting(true);
        try {
            await workspaceApi.deleteWorkspace(currentWorkspace.id);
            // Refresh user data (which includes workspaces list)
            await refreshUser();
            // Context/Auth logic should handle redirection if current workspace is invalid
            // Ideally we'd navigate to a specific place or rely on App.js/Layout to redirect
            // For now, force a reload or navigate root to trigger auth checks
            window.location.href = '/';
        } catch (err) {
            console.error('Failed to delete workspace:', err);
            // Show error in modal or main page? 
            // Better to show in modal but current setup has error state for page
            setError(err.response?.data?.error || 'Failed to delete workspace');
            // Don't close modal so user sees the error
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="general-settings-page">
            <div className="page-header">
                <div className="header-content">
                    <h1>Workspace General Settings</h1>
                    {/* <p>Configure your workspace profile, testing defaults, and global platform preferences.</p> */}
                </div>
            </div>

            <div className="settings-content">
                {/* Workspace Profile Section */}
                <div className="settings-section-row">
                    <div className="section-info">
                        <h2>Workspace Profile</h2>
                        <p>This information will be visible to all members of the workspace.</p>
                    </div>

                    <div className="section-card">
                        {/* Logo Upload */}
                        {/* <div className="form-group logo-group">
                            <label>Workspace Logo</label>
                            <div className="logo-upload-container">
                                <div className="logo-preview">
                                    <div className="logo-placeholder">A</div>
                                </div>
                                <div className="logo-actions">
                                    <button className="btn-upload">Upload new</button>
                                    <button className="btn-remove">Remove</button>
                                    <span className="upload-hint">JPG, PNG or SVG. Max size 2MB.</span>
                                </div>
                            </div>
                        </div> */}

                        {/* Workspace Name */}
                        <div className="form-group">
                            <label htmlFor="workspaceName">Workspace Name</label>
                            <input
                                id="workspaceName"
                                type="text"
                                value={name}
                                onChange={(e) => handleChange(setName, e.target.value)}
                                className="form-input"
                            />
                        </div>

                        {/* Workspace Slug */}
                        {/* <div className="form-group">
                            <label htmlFor="workspaceSlug">Workspace Slug</label>
                            <div className="slug-input-wrapper">
                                <span className="slug-prefix">rova.ai/</span>
                                <input
                                    id="workspaceSlug"
                                    type="text"
                                    value={slug}
                                    onChange={(e) => handleChange(setSlug, e.target.value)}
                                    className="form-input slug-input"
                                />
                            </div>
                            <span className="input-help">Changing the slug will break existing shared links.</span>
                        </div> */}

                        <div className="card-footer">
                            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Divider */}
                {/* <div className="section-divider"></div> */}

                {/* Default Testing Preferences */}
                {/* <div className="settings-section-row">
                    <div className="section-info">
                        <h2>Default Testing Preferences</h2>
                        <p>Global defaults for all new automated test suites in this workspace.</p>
                    </div>

                    <div className="section-card">
                        <div className="form-row">
                            <div className="form-group half">
                                <label htmlFor="primaryOS">Primary Testing OS</label>
                                <select
                                    id="primaryOS"
                                    value={primaryOS}
                                    onChange={(e) => handleChange(setPrimaryOS, e.target.value)}
                                    className="form-input"
                                >
                                    <option value="ios">iOS (iPhone 15 Pro)</option>
                                    <option value="android">Android (Pixel 8)</option>
                                </select>
                            </div>
                            <div className="form-group half">
                                <label htmlFor="orientation">Default Orientation</label>
                                <select
                                    id="orientation"
                                    value={orientation}
                                    onChange={(e) => handleChange(setOrientation, e.target.value)}
                                    className="form-input"
                                >
                                    <option value="portrait">Portrait</option>
                                    <option value="landscape">Landscape</option>
                                </select>
                            </div>
                        </div>

                        <div className="info-box">
                            <Info size={16} className="info-icon" />
                            <p>These settings can still be overridden at the individual test case level.</p>
                        </div>
                    </div>
                </div> */}

                {/* Divider */}
                <div className="section-divider"></div>

                {/* Danger Zone */}
                <div className="settings-section-row danger-row">
                    <div className="section-info">
                        <h2 className="text-danger">Danger Zone</h2>
                        <p>Irreversible actions for this workspace.</p>
                    </div>

                    <div className="section-card danger-card">
                        <div className="danger-content">
                            <div className="danger-text">
                                <h3>Delete this workspace</h3>
                                <p>Once deleted, all data is lost and cannot be recovered.</p>
                            </div>
                            <br />
                            <button
                                className="btn-danger-solid"
                                onClick={() => {
                                    setError(null);
                                    setShowDeleteModal(true);
                                }}
                            >
                                Delete Workspace
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay">
                    <div className="modal-content delete-modal">
                        <div className="modal-header">
                            <h2 className="text-danger">Delete Workspace</h2>
                            <button
                                className="modal-close"
                                onClick={() => setShowDeleteModal(false)}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="alert-banner error">
                                <AlertTriangle size={20} />
                                <div>
                                    <strong>Warning: This action cannot be undone.</strong>
                                    <p>All goals, plans, members, and data associated with <strong>{currentWorkspace.name}</strong> will be permanently lost.</p>
                                </div>
                            </div>

                            {error && (
                                <div className="alert-banner error" style={{ marginBottom: '1rem', marginTop: '0' }}>
                                    <AlertCircle size={20} />
                                    <div>
                                        <strong>Error</strong>
                                        <p>{error}</p>
                                    </div>
                                </div>
                            )}

                            <div className="form-group">
                                <label>To confirm, type "{currentWorkspace.name}" below:</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder={currentWorkspace.name}
                                    value={deleteConfirmationName}
                                    onChange={(e) => setDeleteConfirmationName(e.target.value)}
                                    autoFocus
                                />
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowDeleteModal(false)}
                                disabled={deleting}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-danger-solid"
                                onClick={handleDeleteWorkspace}
                                disabled={deleteConfirmationName !== currentWorkspace.name || deleting}
                            >
                                {deleting ? 'Deleting...' : 'Delete Workspace'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default GeneralSettings;
